/**
 * MARK:- Buy Box Ammo Web Component
 * Enhanced buy box for ammo products with quantity dropdown,
 * rounds calculation, CPR-based savings display.
 */
class BuyBoxAmmo extends HTMLElement {
  constructor() {
    super();
    this.variantData = null;
    this.currentVariantId = null;
    this.baseCpr = 0;
    this.unitLabel = "rounds";
    this.isSimpleMode = false;
  }

  connectedCallback() {
    this.initializeData();
    this.bindElements();
    this.bindEvents();

    // Set initial quantity to 1
    const quantitySelect = this.querySelector('[data-ammo-element="quantity-select"]');
    if (quantitySelect) {
      quantitySelect.value = "1";
    }

    // Emit initial quantity event to trigger display update
    this.handleQuantityChange();
  }

  disconnectedCallback() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
    // MARK:- Clean up document-level event listener
    if (this.boundVariantChangeHandler) {
      document.removeEventListener("product:variant-change", this.boundVariantChangeHandler);
    }
  }

  initializeData() {
    const dataScript = this.querySelector("[data-ammo-variants]");
    if (dataScript) {
      try {
        const data = JSON.parse(dataScript.textContent);
        this.variantData = data.variants;
        // MARK:- Fallback max qty from the default_max_purchase_qty cart setting
        this.defaultMaxQty = data.defaultMaxQty || 99;
        this.baseCpr = data.baseCpr || 0;
        this.isSimpleMode = data.isSimpleMode || false;
        // MARK:- Product-level sale flag (suppresses CPR savings, uses compare_at instead)
        this.anyVariantOnSale = data.anyVariantOnSale || false;
      } catch (e) {
        console.error("BuyBoxAmmo: Failed to parse variant data", e);
      }
    }

    // Also check data attribute for simple mode
    if (this.dataset.simpleMode === "true") {
      this.isSimpleMode = true;
    }

    this.unitLabel = this.dataset.unitLabel || "rounds";
    this.sectionId = this.dataset.sectionId;

    // Get initial variant from form
    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput) {
      this.currentVariantId = variantInput.value;
    }
  }

  bindElements() {
    // Note: quantitySelect is queried fresh where needed to handle potential DOM re-renders
    this.roundsCalc = this.querySelector('[data-ammo-element="rounds-calc"]');
    this.priceDisplay = this.querySelector('[data-ammo-element="price-display"]');
    this.savingsDisplay = this.querySelector('[data-ammo-element="savings-display"]');
    this.addButton = this.querySelector('[data-ammo-element="add-button"]');

    // Value elements
    this.qtyValue = this.querySelector('[data-ammo-value="qty"]');
    this.roundsValue = this.querySelector('[data-ammo-value="rounds"]');
    this.totalRoundsValue = this.querySelector('[data-ammo-value="total-rounds"]');
    this.currentPriceValue = this.querySelector('[data-ammo-value="current-price"]');
    this.comparePriceValue = this.querySelector('[data-ammo-value="compare-price"]');
    this.savingsAmountValue = this.querySelector('[data-ammo-value="savings-amount"]');
    this.savingsPercentValue = this.querySelector('[data-ammo-value="savings-percent"]');
    this.buttonPriceValue = this.querySelector('[data-ammo-value="button-price"]');

    // MARK:- Simple mode elements
    this.saveBadge = this.querySelector('[data-ammo-element="save-badge"]');
    this.stockIndicator = this.querySelector('[data-ammo-element="stock-indicator"]');
  }

  // MARK:- Refresh element references after Section Rendering API DOM replacement
  refreshElements() {
    this.roundsCalc = this.querySelector('[data-ammo-element="rounds-calc"]');
    this.priceDisplay = this.querySelector('[data-ammo-element="price-display"]');
    this.savingsDisplay = this.querySelector('[data-ammo-element="savings-display"]');
    this.addButton = this.querySelector('[data-ammo-element="add-button"]');
    this.qtyValue = this.querySelector('[data-ammo-value="qty"]');
    this.roundsValue = this.querySelector('[data-ammo-value="rounds"]');
    this.totalRoundsValue = this.querySelector('[data-ammo-value="total-rounds"]');
    this.currentPriceValue = this.querySelector('[data-ammo-value="current-price"]');
    this.comparePriceValue = this.querySelector('[data-ammo-value="compare-price"]');
    this.savingsAmountValue = this.querySelector('[data-ammo-value="savings-amount"]');
    this.savingsPercentValue = this.querySelector('[data-ammo-value="savings-percent"]');
    this.buttonPriceValue = this.querySelector('[data-ammo-value="button-price"]');
    this.saveBadge = this.querySelector('[data-ammo-element="save-badge"]');
    this.stockIndicator = this.querySelector('[data-ammo-element="stock-indicator"]');
  }

  bindEvents() {
    // MARK:- Event delegation for quantity select (survives DOM replacement)
    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-ammo-element="quantity-select"]')) {
        this.handleQuantityChange();
      }
    });

    // MARK:- Listen for our own quantity change events to update display
    this.addEventListener('ammo:quantity-change', (e) => this.onQuantityChange(e.detail));

    // Subscribe to variant changes via pubsub
    if (typeof subscribe !== "undefined" && typeof PUB_SUB_EVENTS !== "undefined") {
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.productVariantChange,
        this.handleVariantChange.bind(this)
      );
    }

    // MARK:- Fallback: listen to custom event (store reference for cleanup)
    this.boundVariantChangeHandler = this.handleVariantChangeEvent.bind(this);
    document.addEventListener("product:variant-change", this.boundVariantChangeHandler);
  }

  handleQuantityChange() {
    const quantitySelect = this.querySelector('[data-ammo-element="quantity-select"]');
    const quantity = parseInt(quantitySelect?.value || "1", 10);

    const variantInfo = this.variantData?.[this.currentVariantId];
    if (!variantInfo) return;

    const price = variantInfo.price || 0;
    const rounds = variantInfo.rounds || 0;

    // MARK:- Emit event with all calculated values for event-driven updates
    this.dispatchEvent(new CustomEvent('ammo:quantity-change', {
      bubbles: true,
      detail: {
        sectionId: this.sectionId,
        variantId: this.currentVariantId,
        quantity: quantity,
        unitPrice: price,
        totalPrice: price * quantity,
        rounds: rounds,
        totalRounds: rounds * quantity,
        compareAtPrice: variantInfo.compareAtPrice || 0,
        cpr: variantInfo.cpr || 0,
        // MARK:- Quantity updates use the same purchasable state as variant updates
        available: variantInfo.available && !variantInfo.isDisabled && !variantInfo.isUpsellVariant,
        isDisabled: variantInfo.isDisabled === true,
        mapActive: variantInfo.mapActive === true
      }
    }));
  }

  // MARK:- Event handler for quantity change events
  onQuantityChange(detail) {
    const {
      quantity,
      unitPrice,
      totalPrice,
      rounds,
      totalRounds,
      compareAtPrice,
      cpr,
      available,
      isDisabled,
      mapActive
    } = detail;

    // Update rounds calculation (ammo mode only)
    if (!this.isSimpleMode) {
      if (this.qtyValue) this.qtyValue.textContent = quantity;
      if (this.roundsValue) this.roundsValue.textContent = this.formatNumber(rounds);
      if (this.totalRoundsValue) this.totalRoundsValue.textContent = this.formatNumber(totalRounds);
    }

    // Calculate savings
    let hasSavings = false;
    let totalSavings = 0;
    let comparePrice = 0;
    let savingsPercent = 0;

    // MARK:- Sale mode: compare_at strikethrough only, suppress CPR savings
    if (this.anyVariantOnSale) {
      if (compareAtPrice > unitPrice && unitPrice > 0) {
        hasSavings = true;
        comparePrice = compareAtPrice * quantity;
      }
    } else if (this.isSimpleMode) {
      // Simple mode: use compare_at_price
      if (compareAtPrice > unitPrice) {
        hasSavings = true;
        const unitSavings = compareAtPrice - unitPrice;
        totalSavings = unitSavings * quantity;
        comparePrice = compareAtPrice * quantity;
        savingsPercent = Math.round((unitSavings / compareAtPrice) * 100);
      }
    } else {
      // Ammo mode: CPR-based savings
      if (this.baseCpr > 0 && cpr > 0 && cpr < this.baseCpr) {
        hasSavings = true;
        const cprSavings = this.baseCpr - cpr;
        totalSavings = cprSavings * totalRounds * 100;
        comparePrice = totalPrice + totalSavings;
        savingsPercent = Math.round((cprSavings / this.baseCpr) * 100);
      }
    }

    // MARK:- Hide savings for MAP or non-purchasable variants, including quantity updates
    if (mapActive === true || !available) {
      hasSavings = false;
    }

    // Update price display
    if (this.currentPriceValue) {
      this.currentPriceValue.textContent = this.formatMoney(totalPrice);
    }

    if (this.comparePriceValue) {
      if (hasSavings) {
        this.comparePriceValue.textContent = this.formatMoney(comparePrice);
        this.comparePriceValue.hidden = false;
      } else {
        this.comparePriceValue.hidden = true;
      }
    }

    // Update savings display (ammo mode)
    if (this.savingsDisplay && !this.isSimpleMode) {
      if (hasSavings && !this.anyVariantOnSale) {
        this.savingsDisplay.hidden = false;
        if (this.savingsAmountValue) this.savingsAmountValue.textContent = this.formatMoney(totalSavings);
        if (this.savingsPercentValue) this.savingsPercentValue.textContent = savingsPercent;
      } else {
        this.savingsDisplay.hidden = true;
      }
    }

    // Update save badge (simple mode)
    if (this.saveBadge && this.isSimpleMode) {
      if (hasSavings && !this.anyVariantOnSale) {
        // MARK:- Save % text format resolved per brand via data-save-format
        const saveFormat = this.saveBadge.dataset.saveFormat || "Save [percent]%";
        this.saveBadge.textContent = saveFormat.replace("[percent]", savingsPercent);
        this.saveBadge.parentElement.hidden = false;
      } else {
        this.saveBadge.parentElement.hidden = true;
      }
    }

    // Update stock indicator (simple mode)
    if (this.stockIndicator && this.isSimpleMode) {
      const stockText = this.stockIndicator.querySelector(".buy-box-ammo__stock-text");
      const boltIcon = this.stockIndicator.querySelector("svg");

      if (stockText) {
        if (available) {
          stockText.textContent = "In Stock";
          stockText.className = "buy-box-ammo__stock-text buy-box-ammo__stock-text--in";
          if (boltIcon) boltIcon.style.display = "";
        } else {
          stockText.textContent = isDisabled ? "Unavailable" : "Out of Stock";
          stockText.className = "buy-box-ammo__stock-text buy-box-ammo__stock-text--out";
          if (boltIcon) boltIcon.style.display = "none";
        }
      }
    }

    // Update button price
    if (this.buttonPriceValue) {
      this.buttonPriceValue.textContent = this.formatMoney(totalPrice);
    }
  }

  handleVariantChangeEvent(event) {
    if (event.detail && event.detail.variant) {
      this.onVariantChange(event.detail.variant);
    }
  }

  handleVariantChange(data) {
    // Check if this event is for our section
    if (data.sectionId && data.sectionId !== this.sectionId) {
      return;
    }

    if (data.variant) {
      this.onVariantChange(data.variant);
    }
  }

  onVariantChange(variant) {
    if (!variant) return;

    this.currentVariantId = String(variant.id);

    // MARK:- Refresh DOM references after Section Rendering API replacement
    this.refreshElements();

    const variantInfo = this.variantData?.[this.currentVariantId];

    // MARK:- Check Shopify availability, is_disabled, and is_upsell_variant metafields
    // Upsell variants are post-checkout-only (Aftersell) and must never be purchasable from the PDP.
    const isDisabled = variantInfo?.isDisabled === true;
    const isUpsellVariant = variantInfo?.isUpsellVariant === true;
    const trulyAvailable = variant.available && !isDisabled && !isUpsellVariant;

    // MARK:- Check MAP pricing status
    const mapActive = variantInfo?.mapActive === true;

    // Update variant input in form (re-query since DOM was replaced)
    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = variant.id;
      variantInput.disabled = !trulyAvailable;
    }

    // MARK:- Update limit badge on variant change (respects data-limit-format template with [qty] placeholder)
    const limitBadge = this.querySelector('[data-ammo-element="limit-badge"]');
    if (limitBadge) {
      // Only show badge when maxQty metafield is explicitly set (not null)
      if (variantInfo?.maxQty != null) {
        limitBadge.hidden = false;
        limitBadge.dataset.limit = variantInfo.maxQty;
        const limitSpan = limitBadge.querySelector('span');
        if (limitSpan) {
          const format = limitBadge.dataset.limitFormat || 'Limit [qty] per customer';
          limitSpan.textContent = format.replace('[qty]', variantInfo.maxQty);
        }
      } else {
        limitBadge.hidden = true;
      }
    }

    // MARK:- Update MAP pricing display on variant change
    const mapDisplay = this.querySelector('[data-ammo-element="map-price-display"]');
    const priceDisplay = this.querySelector('[data-ammo-element="price-display"]');
    const savingsDisplay = this.querySelector('[data-ammo-element="savings-display"]');
    const saveBadgeWrapper = this.querySelector('.buy-box-ammo__save-badge-wrapper');
    const buttonSeparator = this.addButton?.querySelector('.buy-box-ammo__button-separator');
    const buttonPrice = this.addButton?.querySelector('.buy-box-ammo__button-price');

    // MARK:- Non-purchasable variants must not expose prices or savings
    if (!trulyAvailable) {
      if (mapDisplay) mapDisplay.hidden = true;
      if (priceDisplay) priceDisplay.hidden = true;
      if (savingsDisplay) savingsDisplay.hidden = true;
      if (saveBadgeWrapper) saveBadgeWrapper.hidden = true;
      if (buttonSeparator) buttonSeparator.hidden = true;
      if (buttonPrice) buttonPrice.hidden = true;
    } else if (mapActive) {
      if (mapDisplay) mapDisplay.hidden = false;
      if (priceDisplay) priceDisplay.hidden = true;
      if (savingsDisplay) savingsDisplay.hidden = true;
      if (saveBadgeWrapper) saveBadgeWrapper.hidden = true;
      if (buttonSeparator) buttonSeparator.hidden = true;
      if (buttonPrice) buttonPrice.hidden = true;

      // Update MAP MSRP if present
      const mapMsrp = this.querySelector('[data-ammo-value="map-msrp"]');
      const mapMsrpWrapper = mapMsrp?.closest('.buy-box-ammo__map-msrp');
      if (mapMsrp && variantInfo?.compareAtPrice > 0) {
        mapMsrp.textContent = this.formatMoney(variantInfo.compareAtPrice);
        if (mapMsrpWrapper) mapMsrpWrapper.hidden = false;
      } else if (mapMsrpWrapper) {
        mapMsrpWrapper.hidden = true;
      }
    } else {
      if (mapDisplay) mapDisplay.hidden = true;
      if (priceDisplay) priceDisplay.hidden = false;
      if (buttonSeparator) buttonSeparator.hidden = false;
      if (buttonPrice) buttonPrice.hidden = false;
    }

    // Update button state
    if (this.addButton) {
      this.addButton.disabled = !trulyAvailable;

      // Update button text
      const buttonText = this.addButton.querySelector(".buy-box-ammo__button-text");
      if (buttonText) {
        if (isDisabled) {
          buttonText.textContent = "Unavailable";
        } else if (!variant.available) {
          buttonText.textContent =
            window.variantStrings?.outOfStock ||
            window.themeStrings?.outOfStock ||
            "Out of stock";
        } else if (mapActive) {
          buttonText.textContent = this.addButton.dataset.mapText || "ADD TO CART FOR PRICE";
        } else if (variant.inventory_policy === "continue" && variant.inventory_quantity <= 0) {
          buttonText.textContent = window.variantStrings?.backorder || "Backorder";
        } else {
          buttonText.textContent =
            window.variantStrings?.addToCart || window.themeStrings?.addToCart || "Add To Cart";
        }
      }
    }

    // MARK:- Update stock indicator for is_disabled
    if (this.stockIndicator && this.isSimpleMode) {
      const stockText = this.stockIndicator.querySelector(".buy-box-ammo__stock-text");
      const boltIcon = this.stockIndicator.querySelector("svg");

      if (stockText) {
        if (trulyAvailable) {
          stockText.textContent = "In Stock";
          stockText.className = "buy-box-ammo__stock-text buy-box-ammo__stock-text--in";
          if (boltIcon) boltIcon.style.display = "";
        } else {
          stockText.textContent = isDisabled ? "Unavailable" : "Out of Stock";
          stockText.className = "buy-box-ammo__stock-text buy-box-ammo__stock-text--out";
          if (boltIcon) boltIcon.style.display = "none";
        }
      }
    }

    // Rebuild quantity options and update display
    this.rebuildQuantityOptions();
    this.updateDisplay();
  }

  rebuildQuantityOptions() {
    // Query quantity select fresh to handle potential DOM re-renders
    const quantitySelect = this.querySelector('[data-ammo-element="quantity-select"]');
    if (!quantitySelect || !this.variantData || !this.currentVariantId) return;

    const variantInfo = this.variantData[this.currentVariantId];
    if (!variantInfo) return;

    // MARK:- Use null-coalescing for metafield values (null = no limit set)
    let minQty = variantInfo.minQty ?? 1;
    let maxQty = variantInfo.maxQty ?? (this.defaultMaxQty ?? 99);

    // Ensure valid range
    if (minQty < 1) minQty = 1;
    if (maxQty < minQty) maxQty = minQty;

    // Clear and rebuild options
    quantitySelect.innerHTML = "";

    for (let i = minQty; i <= maxQty; i++) {
      const option = document.createElement("option");
      option.value = i;
      option.textContent = i;
      quantitySelect.appendChild(option);
    }

    // MARK:- Always reset to minQty on variant change (user is likely comparison shopping)
    quantitySelect.value = minQty;
  }

  updateDisplay() {
    // Delegate to event-driven handler
    this.handleQuantityChange();
  }

  formatNumber(num) {
    return num.toLocaleString();
  }

  formatMoney(cents) {
    // Use Shopify's money format if available
    if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || "${{amount}}");
    }

    // MARK:- Fallback formatting with proper comma separators
    const amount = (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    const amountNoDecimals = Math.floor(cents / 100).toLocaleString('en-US');
    const moneyFormat = window.theme?.moneyFormat || "${{amount}}";

    return moneyFormat
      .replace("{{amount}}", amount)
      .replace("{{amount_no_decimals}}", amountNoDecimals)
      .replace("{{amount_with_comma_separator}}", amount.replace(",", ".").replace(".", ","))
      .replace("{{amount_no_decimals_with_comma_separator}}", amountNoDecimals.replace(",", "."))
      .replace("{{amount_with_apostrophe_separator}}", amount.replace(",", "'"));
  }
}

// Register the custom element
if (!customElements.get("buy-box-ammo")) {
  customElements.define("buy-box-ammo", BuyBoxAmmo);
}
